/**
 * TrackingPage - Secure booking tracking page with OTP verification
 * Flow: Reference → Email → OTP Verification → View Booking
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Search, Loader2, AlertTriangle, ArrowLeft, Mail, KeyRound, CheckCircle, ChevronRight } from 'lucide-react';
import axios from 'axios';
import BookingDetailModal from '../../components/client/BookingDetailModal';
import { BrandMark } from '../../components/BrandMark';
import { SEO } from '../../components/SEO';
import config from 'config';

const API_URL = config.API_URL;

const TrackingPage = () => {
  const [searchParams] = useSearchParams();

  // Step management: 'reference' | 'email' | 'otp' | 'done'
  const [step, setStep] = useState('reference');

  const [reference, setReference] = useState(() => (searchParams.get('ref') || '').toUpperCase());
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [booking, setBooking] = useState(null);
  const [expiresIn, setExpiresIn] = useState(10);

  // Auto-submit when arriving with a ?ref= URL param (from email link)
  const autoSubmittedRef = useRef(false);

  // Step 1: Enter reference and fetch booking directly (OTP DISABLED FOR TESTING)
  const handleReferenceSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const ref = (reference || '').trim().toUpperCase();
    if (!ref) {
      setError('Veuillez entrer un numéro de réservation');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Direct booking access without OTP verification
      const response = await axios.get(`${API_URL}/api/bookings/track/${ref}`);
      setBooking(response.data);
      setStep('done');
    } catch (err) {
      console.error('Tracking error:', err);
      setError(err.response?.data?.detail || 'Réservation non trouvée');
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit if the user arrived with a ?ref= param (email deep link)
  useEffect(() => {
    const refFromUrl = (searchParams.get('ref') || '').trim();
    if (refFromUrl && !autoSubmittedRef.current && step === 'reference') {
      autoSubmittedRef.current = true;
      handleReferenceSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Step 2: Enter email and request OTP
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Veuillez entrer votre adresse email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/bookings/otp/request`, {
        booking_number: reference.trim().toUpperCase(),
        email: email.trim().toLowerCase()
      });
      
      setExpiresIn(response.data.expires_in_minutes);
      setSuccessMessage(response.data.message);
      setStep('otp');
    } catch (err) {
      console.error('OTP request error:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verify OTP and get booking
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otpCode.trim() || otpCode.length !== 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Verify OTP and get booking in one call
      const response = await axios.post(`${API_URL}/api/bookings/otp/access`, {
        booking_number: reference.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        code: otpCode.trim()
      });
      
      setBooking(response.data);
      setStep('done');
    } catch (err) {
      console.error('OTP verify error:', err);
      setError(err.response?.data?.detail || 'Code invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/bookings/otp/request`, {
        booking_number: reference.trim().toUpperCase(),
        email: email.trim().toLowerCase()
      });
      
      setExpiresIn(response.data.expires_in_minutes);
      setSuccessMessage('Un nouveau code a été envoyé');
      setOtpCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du renvoi du code');
    } finally {
      setLoading(false);
    }
  };

  // Reset and start over
  const handleReset = () => {
    setStep('reference');
    setReference('');
    setEmail('');
    setOtpCode('');
    setError(null);
    setSuccessMessage(null);
    setBooking(null);
  };

  // Refresh booking data
  const handleRefresh = async () => {
    if (!reference.trim()) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/bookings/otp/access`, {
        booking_number: reference.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        code: otpCode.trim()
      });
      setBooking(response.data);
    } catch (err) {
      console.error('Refresh error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-4" data-testid="tracking-page">
      <SEO title="Suivi de réservation VTC" description="Suivez votre réservation Y GO en temps réel : entrez votre numéro de réservation pour connaître l'état de votre transfert VTC au Havre." />
      <Card className="w-full max-w-md bg-[#1F1F1F] border-[#C9A24D]/10">
        <CardContent className="p-6">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <BrandMark size="xl" className="justify-center" />
            </Link>
            <h1 className="text-xl font-semibold text-[#E5E5E5]">
              Suivi de réservation
            </h1>
            <p className="text-[#E5E5E5]/60 text-sm mt-2">
              {step === 'reference' && 'Entrez votre numéro de réservation'}
              {step === 'done' && 'Votre réservation'}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {/* Success message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-green-400 text-sm">{successMessage}</span>
            </div>
          )}

          {/* Step 1: Reference */}
          {step === 'reference' && (
            <form onSubmit={handleReferenceSubmit} className="space-y-4">
              <div>
                <label className="block text-[#E5E5E5]/60 text-sm mb-2">
                  Numéro de réservation
                </label>
                <Input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value.toUpperCase())}
                  placeholder="Ex: 8F461V8V"
                  className="bg-[#0B0B0B] border-[#C9A24D]/30 text-[#E5E5E5] text-center text-lg font-mono tracking-wider"
                  autoFocus
                  data-testid="reference-input"
                />
              </div>
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
                data-testid="reference-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Rechercher
              </Button>
            </form>
          )}

          {/* OTP Steps temporarily disabled for testing */}
          {/* 
          Step 2: Email 
          {step === 'email' && (...)}
          
          Step 3: OTP
          {step === 'otp' && (...)}
          */}

          {/* Step 2: Done - show booking in modal */}
          {step === 'done' && booking && (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-[#E5E5E5] mb-4">Réservation trouvée !</p>
              <Button
                onClick={handleReset}
                variant="outline"
                className="border-[#C9A24D]/30 text-[#C9A24D] hover:bg-[#C9A24D]/10"
              >
                Nouvelle recherche
              </Button>

              {/* Upsell: create account to centralize bookings */}
              <div className="mt-6 p-3 rounded-lg bg-[#C9A24D]/5 border border-[#C9A24D]/20" data-testid="tracking-upsell-account">
                <p className="text-xs text-[#E5E5E5]/60 mb-2">
                  Envie de retrouver automatiquement toutes vos réservations&nbsp;?
                </p>
                <Link to="/register">
                  <Button variant="ghost" className="text-[#C9A24D] hover:bg-[#C9A24D]/10 text-xs">
                    Créer un compte gratuit
                    <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Footer links */}
          {step === 'reference' && (
            <div className="flex gap-3 justify-center mt-6">
              <Link to="/login">
                <Button variant="outline" className="border-[#C9A24D]/30 text-[#C9A24D] hover:bg-[#C9A24D]/10">
                  Se connecter
                </Button>
              </Link>
              <Link to="/booking">
                <Button variant="ghost" className="text-[#E5E5E5]/60 hover:text-[#C9A24D]">
                  Nouvelle réservation
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Detail Modal */}
      {booking && (
        <BookingDetailModal 
          booking={booking}
          onClose={handleReset}
          onRefresh={handleRefresh}
        />
      )}
    </div>
  );
};

export default TrackingPage;
